import React, {
  useState,
  RefObject,
  useEffect,
  forwardRef,
  useImperativeHandle,
  ForwardRefRenderFunction,
} from 'react';
import {
  Grid,
  Radio,
  RadioGroup,
  Typography,
  FormControlLabel,
} from '@mui/material';

import { CalendarView } from '../helper';
import { Ref as DateRangePickerRef } from './DateRangePicker';
import { PreDefinedGranularityFilterOption } from 'types/types';
import { Ref as DateRangeCalendarRef } from './DateRangeCalendar';
import { Ref as PreDefinedCalendarFiltersRef } from './PreDefinedCalendarFilters';
import { Ref as DataSpecificAndCustomFilterRef } from './DataSpecificAndCustomFilter';

interface Props {
  selectedGranularityValue?: string;
  dateRangePickerRef: RefObject<DateRangePickerRef>;
  dateRangeCalendarRef: RefObject<DateRangeCalendarRef>;
  granularityOptions: PreDefinedGranularityFilterOption[];
  preDefinedCalendarFiltersRef: RefObject<PreDefinedCalendarFiltersRef>;
  dataSpecificAndCustomFilterRef: RefObject<DataSpecificAndCustomFilterRef>;
}

export interface Ref {
  selectedGranularity?: PreDefinedGranularityFilterOption;
}
const DataGranularities: ForwardRefRenderFunction<Ref, Props> = (
  {
    granularityOptions,
    dateRangePickerRef,
    dateRangeCalendarRef,
    selectedGranularityValue,
    preDefinedCalendarFiltersRef,
    dataSpecificAndCustomFilterRef,
  },
  ref
) => {
  const [selectedGranularity, setSelectedGranularity] =
    useState<PreDefinedGranularityFilterOption>();

  const handleGranularityAndAllComponentsChange = (
    granularityFilterOption: PreDefinedGranularityFilterOption | undefined
  ) => {
    setSelectedGranularity(granularityFilterOption);

    dateRangeCalendarRef.current?.handleCalendarView(
      granularityFilterOption?.calendarView ?? CalendarView.DAY
    );

    dateRangePickerRef.current?.handleCalendarView(
      granularityFilterOption?.calendarView ?? CalendarView.DAY
    );

    preDefinedCalendarFiltersRef.current?.handleChangeOptions(
      granularityFilterOption
    );

    dataSpecificAndCustomFilterRef.current?.handleShow(
      granularityFilterOption?.showSpecificAndCustomFilters ?? false
    );

    setTimeout(() => {
      dataSpecificAndCustomFilterRef.current?.customFilter.current?.handleHideMinutesOption(
        granularityFilterOption?.value.toLowerCase() === 'hourly'
      );
    }, 1);
  };

  const handleGranularityChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selectedElement = granularityOptions.find(
      (el) => el.value === event.target.value
    );
    handleGranularityAndAllComponentsChange(selectedElement);
  };

  useImperativeHandle(ref, () => ({
    selectedGranularity,
  }));

  useEffect(() => {
    if (selectedGranularityValue && granularityOptions) {
      const foundElement = granularityOptions.find(
        (el) =>
          el.value === selectedGranularityValue ||
          el.label === selectedGranularityValue
      );
      handleGranularityAndAllComponentsChange(foundElement);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGranularityValue, granularityOptions]);

  return (
    <React.Fragment>
      <Typography className="granularity-form-element-label">
        Data Granularity
      </Typography>
      <div className="radio-buttons-details-data-granularity-container">
        <RadioGroup name="granularity" row onChange={handleGranularityChange}>
          <Grid container>
            {granularityOptions?.map((el) => (
              <Grid item xs={4} key={`Granularity-${el.value}`}>
                <FormControlLabel
                  value={el.value}
                  control={
                    <Radio
                      className="radio-button"
                      checked={el.value === selectedGranularity?.value}
                    />
                  }
                  label={el.label}
                />
              </Grid>
            ))}
          </Grid>
        </RadioGroup>
      </div>
    </React.Fragment>
  );
};

export default forwardRef<Ref, Props>(DataGranularities);
